import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatKpiAdaptiveINR } from '../utils/kpiAmountFormat';

const formatCell = (col, row) => {
  const v = row[col.key];
  if (v == null || v === '') return '—';
  if (col.format === 'inr') return formatKpiAdaptiveINR(Number(v));
  if (col.format === 'int') return new Intl.NumberFormat('en-IN').format(Math.round(Number(v)));
  return String(v);
};

/**
 * Hover popover with a structured table (e.g. project × amount) for KPI cards.
 * Renders in a portal with fixed positioning so it is not clipped.
 */
const KpiBreakdownTooltip = ({
  title,
  subtitle,
  columns,
  rows = [],
  emptyMessage = 'No breakdown available.',
  maxRows = 28,
  children,
}) => {
  const anchorRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ left: 0, top: 0 });

  const updatePosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cardMaxW = 520;
    const left = Math.max(12, Math.min(r.left, window.innerWidth - cardMaxW - 12));
    const top = r.top - 10;
    setCoords({ left, top });
  }, []);

  const show = useCallback(() => {
    updatePosition();
    setVisible(true);
  }, [updatePosition]);

  const hide = useCallback(() => {
    setVisible(false);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const onReposition = () => hide();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [visible, hide]);

  const displayRows = rows.slice(0, maxRows);
  const truncated = rows.length > maxRows;

  const card = visible && (
    <div
      className="kpi-breakdown-tooltip-card"
      style={{
        position: 'fixed',
        left: coords.left,
        top: coords.top,
      }}
      role="tooltip"
    >
      <div className="kpi-breakdown-tooltip-arrow" aria-hidden />
      <div className="kpi-breakdown-tooltip-header">
        <strong className="kpi-breakdown-tooltip-title">{title}</strong>
        {subtitle ? <span className="kpi-breakdown-tooltip-sub">{subtitle}</span> : null}
      </div>
      {displayRows.length === 0 ? (
        <p className="kpi-breakdown-tooltip-empty">{emptyMessage}</p>
      ) : (
        <div className="kpi-breakdown-tooltip-scroll">
          <table className="kpi-breakdown-tooltip-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className={c.align === 'right' ? 'kpi-breakdown-tooltip-th-num' : ''}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row, i) => (
                <tr key={row.projectId != null ? String(row.projectId) : `r-${i}`}>
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={c.align === 'right' ? 'kpi-breakdown-tooltip-td-num' : ''}
                    >
                      {formatCell(c, row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {truncated ? (
        <p className="kpi-breakdown-tooltip-footer">
          Showing {maxRows} of {rows.length} rows.
        </p>
      ) : null}
    </div>
  );

  return (
    <>
      <div
        ref={anchorRef}
        className="kpi-breakdown-tooltip-anchor"
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        {children}
      </div>
      {typeof document !== 'undefined' && createPortal(card, document.body)}
    </>
  );
};

export default KpiBreakdownTooltip;
